/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */

import { Button, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconRestore, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SortDirection, Template, TemplatesCriteria } from '@e-origin/shared';

import { useAdminTemplates, useDeleteTemplate, usePublishTemplate } from '@admin/hooks';
import { CheckedColumn, Table, TableActionEnum, TableActions } from '@components';
import { TABLE_MENU_THRESHOLD } from '@constants';
import { useSettings } from '@hooks/useSettings';
import { DiffTemplates } from './DiffTemplates';

export const TemplateVersions = ({ versioningId, templateType, editDisabled }) => {
  const theme = useMantineTheme();
  const navigate = useNavigate();

  const { data: settings } = useSettings();

  const deleteTemplateMutation = useDeleteTemplate();
  const publishTemplateMutation = usePublishTemplate();

  const [sorting, setSorting] = useState<TemplatesCriteria['sorting']>({ field: 'version', direction: SortDirection.DESC });
  const [pagination, setPagination] = useState<TemplatesCriteria['pagination']>({ page: 1, size: 10 });

  const { data: { list: templatesList, total: totalTemplates } = {}, isLoading: isTemplateListLoading } = useAdminTemplates({
    filters: { versioningId },
    sorting,
    pagination,
  });

  const openDiff = (templates) => {
    modals.open({
      title: 'Diff templates',
      size: '1400px',
      children: <DiffTemplates templates={templates} />,
    });
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  const handleTableAction = async (actionType: string, row?: Template) => {
    switch (actionType) {
      case TableActionEnum.Publish: {
        modals.openConfirmModal({
          title: 'Confirm version publish',
          children: (
            <Text>
              Are you sure you want to publish this version? <br />
            </Text>
          ),
          labels: { confirm: 'Publish', cancel: 'Cancel' },
          confirmProps: { color: 'red' },
          onConfirm: async () => {
            await publishTemplateMutation.mutateAsync(row._id);
            navigate(`/${settings.adminPath}/manage-templates/edit/${templateType}/${row._id}`, { replace: true });
            modals.closeAll();
          },
        });
        break;
      }
      case TableActionEnum.Delete: {
        modals.openConfirmModal({
          title: 'Confirm template delete',
          children: (
            <Text>
              Are you sure you want to delete the template? <br />
              This action cannot be undone!
            </Text>
          ),
          labels: { confirm: 'Delete', cancel: 'Cancel' },
          confirmProps: { color: 'red' },
          onConfirm: async () => {
            await deleteTemplateMutation.mutateAsync(row._id);
            modals.closeAll();
          },
        });
        break;
      }
      default:
        break;
    }
  };

  const columns = [
    {
      name: 'Template name',
      selector: 'name',
      cell: (row) => row.name,
    },
    {
      name: 'Template id',
      selector: '_id',
      cell: (row) => row._id,
    },
    {
      name: 'Version',
      selector: 'version',
      cell: (row) => row.version,
      sortable: true,
    },
    {
      name: 'Published',
      selector: 'published',
      cell: (row) => <CheckedColumn checked={row.published} />,
    },
    ...(!editDisabled
      ? [
          {
            name: 'Actions',
            sticky: 'right' as any,
            cell: (row, rowIdx) => (
              <TableActions
                actions={[
                  {
                    if: !row.published,
                    iconComponent: <IconTrash color={theme.colors[theme.primaryColor][6]} />,
                    text: 'Delete',
                    onClick: () => handleTableAction(TableActionEnum.Delete, row),
                  },
                  {
                    if: !row.published,
                    iconComponent: <IconRestore color={theme.colors[theme.primaryColor][6]} />,
                    text: 'Publish',
                    onClick: () => handleTableAction(TableActionEnum.Publish, row),
                  },
                ]}
                disableAfterClick={false}
                moveMenuUpwards={pagination.size - rowIdx <= TABLE_MENU_THRESHOLD}
                data-pw={rowIdx}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Stack h="calc(100vh - 500px)">
      {templatesList?.length > 1 && (
        <Group>
          <Button type="button" variant="filled" size="lg" onClick={() => openDiff(templatesList)} my={32}>
            Open diff
          </Button>
        </Group>
      )}

      <Table
        columns={columns}
        dataSource={templatesList || []}
        total={totalTemplates}
        onPaginationChange={handlePagination}
        onSortingChange={handleSorting}
        isLoading={isTemplateListLoading}
        defaultPage={pagination.page}
        defaultPerPage={pagination.size}
        defaultSorting={sorting.field}
        defaultSortingDirection={sorting.direction}
      />
    </Stack>
  );
};
